
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../../../../i18n";
import { Schemes, Utils } from "../../../../../../utils";
import { ConfirmDialog, Siren } from "../../../../../shared/ConfirmDialog";
import { BaseService, ITournamentEditionMatch, ITournamentEditionRegisteredParticipant, SanitizedResponse, TournamentService } from "../../../../../../services";
import { FormControl, TextInput, ButtonManageRef, DateTimeInput, Dropdown, AlertInterface, ObjectHelper, DOMHelper, Classname, DropdownManageRef, DateHelper } from "@rivtn/noseur";

export function CreateUpdateMatch(tournamentEditionId: string, onDone: (tournamentMatch: ITournamentEditionMatch) => void, tournamentMatch?: ITournamentEditionMatch, fromBucket?: boolean) {
    const isUpdate = !!tournamentMatch;
    const dialogErrorRef = { current: null } as any;
    const dialogDurationRef = { current: null } as any;
    const activeDialogMutableRoot = { current: null } as any;
    const tournamentService = TournamentService.getInstance();
    const dialogLabels = i18nManager.Labels.host.manage_tournament.match_fixtures.dialog.add_update_match;
    let selectedParticipants: ITournamentEditionRegisteredParticipant[] = tournamentMatch?.tournament_edition_registered_participants?.map((p) => p as any) ?? [];
    if (!tournamentMatch) tournamentMatch = {} as any;

    const updateSelectedParticipants = () => {
        if (!activeDialogMutableRoot.current) return;
        tournamentMatch!.tournament_edition_registered_participant_ids = selectedParticipants.map((p) => p.external_id);
        const permissions = selectedParticipants.map((selectedParticipant: ITournamentEditionRegisteredParticipant, index: number) => {
            const name = Utils.getResourceName(selectedParticipant.resource);
            const imageUrl = Utils.getResourceImage(selectedParticipant.resource);

            return (<div key={selectedParticipant.external_id} style={{ display: "flex", alignItems: "center", marginTop: 15 }} ref={(r: any) => r?.scrollIntoView()}>
                <i className={Classname.build("fa fa-trash noseur-danger-tx", { "noseur-disabled": selectedParticipants.length === 1 })} style={{ cursor: "pointer" }} onClick={() => {
                    selectedParticipants.splice(index, 1);
                    //console.log("---", selectedParticipants);
                    updateSelectedParticipants();
                }} />
                <div style={{ display: "flex", alignItems: "center", width: "max-content" }}>
                    <img alt={name} src={imageUrl} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                    <span>{name}</span>
                </div>
            </div>);
        });
        activeDialogMutableRoot.current.render(permissions);
    }

    function searchParticipants(search: string, onResult: (content: ITournamentEditionRegisteredParticipant[]) => void) {
        tournamentService.queryTournamentEditionRegisteredParticipants(tournamentEditionId, {
            size: 20,
            "extra_data.participant_name": search,
            "tournament_edition_registered.status": "REGISTERED",
        }).then(({ content }: SanitizedResponse<any>) => {
            onResult(content);
        }).catch(BaseService.reportError);
    }

    function showDuration() {
        if (!tournamentMatch?.start_date || !tournamentMatch?.end_date || !dialogDurationRef.current) return;
        dialogDurationRef.current.innerText = DateHelper.formatDuration(new Date(tournamentMatch?.start_date), new Date(tournamentMatch?.end_date));
    }

    const dialog = ConfirmDialog({
        closable: true,
        confirmScheme: Schemes.OMNEHOST,
        title: (isUpdate ? dialogLabels.update_match : dialogLabels.add_new),
        confirmLabel: (isUpdate ? dialogLabels.update_match : dialogLabels.create_match),
        onLoading: async (alert: AlertInterface) => {
            searchParticipants("", (participants) => {
                alert.doneLoading(() => {
                    alert.update({
                        __pass_through__: true,
                        style: { minWidth: "50%" },
                        content: (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                            {isUpdate
                                ? (fromBucket
                                    ? (<span>{dialogLabels.update_this_match}</span>)
                                    : (<span>
                                        {dialogLabels.update_this_match}{" "}
                                        <span className="link danger" style={{ cursor: "text" }}>{dialogLabels.bracket_update_affection}</span>
                                    </span>))
                                : (fromBucket
                                    ? (<span>{dialogLabels.create_in_bracket}</span>)
                                    : (<span>
                                        {dialogLabels.create_new}{" "}
                                        <span className="link danger" style={{ cursor: "text" }}>{dialogLabels.bracket_affection}</span>
                                    </span>))}
                            <FormControl isFieldset fill label={dialogLabels.match} style={{ marginTop: 15 }}>
                                <TextInput disabled scheme={Schemes.OMNEHOST}
                                    defaultValue={dialogLabels.match_value
                                        .replace("${group_number}", "" + (tournamentMatch?.group_number ?? "X"))
                                        .replace("${match_number}", "" + (tournamentMatch?.match_number ?? "X"))}
                                    style={{ width: "100%" }} />
                            </FormControl>
                            <FormControl fill className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} label={dialogLabels.start_date} rightContent={"fa fa-calendar"}>
                                <DateTimeInput fill highlightToday selectedDates={tournamentMatch?.start_date ? [new Date(tournamentMatch.start_date)] : undefined}
                                    onSelectDate={(options) => {
                                        dialogErrorRef.current!.innerText = " ";
                                        tournamentMatch!.start_date = options.selectedDate!.toISOString();
                                        showDuration();
                                    }} />
                            </FormControl>
                            <FormControl fill className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} label={dialogLabels.end_date} rightContent={"fa fa-calendar"}>
                                <DateTimeInput fill highlightToday selectedDates={tournamentMatch?.end_date ? [new Date(tournamentMatch.end_date)] : undefined}
                                    onSelectDate={(options) => {
                                        dialogErrorRef.current!.innerText = " ";
                                        tournamentMatch!.end_date = options.selectedDate!.toISOString();
                                        showDuration();
                                    }} />
                            </FormControl>
                            <FormControl isFieldset fill label={dialogLabels.location} style={{ marginTop: 15 }}>
                                <TextInput scheme={Schemes.OMNEHOST} defaultValue={tournamentMatch?.location} style={{ width: "100%" }} onInputComplete={(v) => {
                                    dialogErrorRef.current!.innerText = " ";
                                    tournamentMatch!.location = v;
                                }} />
                            </FormControl>
                            <span style={{ marginTop: 15 }}>{dialogLabels.duration} <span ref={dialogDurationRef} /></span>
                            <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
                        </div>),
                        rightContent: (<div style={{ borderLeft: "1px solid #E3E3E3", height: "100%", paddingLeft: 15, overflow: "auto" }} ref={showDuration}>
                            <Dropdown fill editable scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: dialogLabels.search_and_select_participant, style: { marginTop: 10 } }}
                                options={participants.map((v) => {
                                    (v as any).label = Utils.getResourceName(v.resource);
                                    (v as any).icon = Utils.getResourceImage(v.resource);
                                    return v;
                                })} onSearch={(e, dropdownManageRef?: DropdownManageRef) => {
                                    searchParticipants(e.target.value, (participants) => {
                                        dropdownManageRef?.changeOptions(participants.map((v) => {
                                            (v as any).label = Utils.getResourceName(v.resource);
                                            (v as any).icon = Utils.getResourceImage(v.resource);
                                            return v;
                                        }));
                                    });
                                    return undefined;
                                }}
                                onSelectOption={(option) => {
                                    if (selectedParticipants.findIndex((p) => p.external_id === option.external_id) < 0) {
                                        selectedParticipants.push(option);
                                        updateSelectedParticipants();
                                    }
                                    return true;
                                }} />
                            <div style={{ flex: 1 }} ref={(r: any) => {
                                if (!r) return;
                                ObjectHelper.resolveRef(activeDialogMutableRoot, ReactDOM.createRoot(r));
                                updateSelectedParticipants();
                            }}></div>
                        </div>),
                    } as any);
                });
            });
            return false;
        },
        onConfirm: async (button?: ButtonManageRef) => {
            button?.setLoadingState(true);
            const method = (isUpdate ? tournamentService.updateTournamentEditionMatch : tournamentService.createTournamentEditionMatch).bind(tournamentService);
            method(tournamentEditionId, tournamentMatch!).then(({ sanitized }: SanitizedResponse<any>) => {
                dialog.hide(() => {
                    onDone(sanitized)
                    Siren.alert(isUpdate ? dialogLabels.update_successful : dialogLabels.create_successful);
                });
            }).catch(({ errorMessage }: any) => {
                dialogErrorRef.current!.innerText = errorMessage;
                button?.setLoadingState(false);
            });
            return false;
        }
    });
    dialog.show();
}